import { useState, useEffect, useMemo } from "react";
import useLearningPlatform from "../../../api/learningPlatform";
import { Search, User, Mail, BookOpen, Loader2, Calendar } from "lucide-react";
import {
  inputClass,
  SectionLoader,
  EmptyState,
  Modal,
  isNotFoundError,
} from "./shared";

const StudentsTab = ({ isDark }) => {
  const { getStudents } = useLearningPlatform();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getStudents();
        if (!cancelled) setStudents(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled && !isNotFoundError(e)) {
          setError("Kon studenten niet laden");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getStudents]);

  const filtered = useMemo(() => {
    if (!query.trim()) return students;
    const q = query.toLowerCase();
    return students.filter(
      (s) =>
        s.name?.toLowerCase().includes(q) ||
        s.email?.toLowerCase().includes(q),
    );
  }, [students, query]);

  if (loading) return <SectionLoader isDark={isDark} />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3
            className={`text-[14px] font-semibold ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            Studenten
          </h3>
          <p
            className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
          >
            {students.length} ingeschreven
          </p>
        </div>
        <div className="relative max-w-xs flex-1">
          <Search
            size={13}
            className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${
              isDark ? "text-gray-600" : "text-gray-400"
            }`}
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Zoek op naam of email"
            className={`${inputClass(isDark)} pl-8`}
          />
        </div>
      </div>

      {error && (
        <div
          className={`text-[12px] px-3 py-2 rounded-md ${
            isDark ? "bg-red-500/10 text-red-400" : "bg-red-50 text-red-600"
          }`}
        >
          {error}
        </div>
      )}

      {students.length === 0 ? (
        <EmptyState
          isDark={isDark}
          title="Nog geen studenten"
          description="Studenten verschijnen hier wanneer ze zich inschrijven via je leerplatform."
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          isDark={isDark}
          title="Geen resultaten"
          description={`Geen studenten die overeenkomen met "${query}".`}
        />
      ) : (
        <div
          className={`rounded-lg border overflow-hidden ${
            isDark ? "border-white/[0.06]" : "border-gray-200"
          }`}
        >
          <table className="w-full text-[12px]">
            <thead
              className={`text-left ${
                isDark
                  ? "bg-white/[0.02] text-gray-500"
                  : "bg-gray-50 text-gray-500"
              }`}
            >
              <tr>
                <Th>Naam</Th>
                <Th>Email</Th>
                <Th className="text-right">Cursussen</Th>
                <Th>Laatst actief</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((student) => (
                <tr
                  key={student.id}
                  onClick={() => setSelected(student)}
                  className={`cursor-pointer transition-colors border-t ${
                    isDark
                      ? "border-white/[0.04] hover:bg-white/[0.02]"
                      : "border-gray-100 hover:bg-gray-50"
                  }`}
                >
                  <Td>
                    <div className="flex items-center gap-2">
                      <div
                        className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-semibold ${
                          isDark
                            ? "bg-white/[0.08] text-gray-300"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {student.name?.charAt(0)?.toUpperCase() || "?"}
                      </div>
                      <span
                        className={isDark ? "text-gray-200" : "text-gray-900"}
                      >
                        {student.name || "—"}
                      </span>
                    </div>
                  </Td>
                  <Td>{student.email}</Td>
                  <Td className="text-right">
                    {student.courses_count || 0}
                  </Td>
                  <Td>
                    {student.last_active
                      ? formatRelative(student.last_active)
                      : "—"}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <StudentDetailModal
          student={selected}
          isDark={isDark}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
};

const StudentDetailModal = ({ student, isDark, onClose }) => {
  const { getStudentProgress } = useLearningPlatform();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getStudentProgress(student.id);
        if (!cancelled) setProgress(data);
      } catch (e) {
        if (!cancelled && !isNotFoundError(e)) {
          // ignore
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [student.id, getStudentProgress]);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={student.name || student.email}
      isDark={isDark}
      maxWidth="max-w-xl"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 text-[12px]">
          <Stat
            isDark={isDark}
            icon={Mail}
            label="Email"
            value={student.email}
          />
          <Stat
            isDark={isDark}
            icon={Calendar}
            label="Aangemeld"
            value={student.created_at ? formatDate(student.created_at) : "—"}
          />
        </div>

        <div>
          <h4
            className={`text-[12px] font-semibold mb-2 ${
              isDark ? "text-gray-300" : "text-gray-700"
            }`}
          >
            Voortgang per cursus
          </h4>
          {loading ? (
            <div className="py-4 flex justify-center">
              <Loader2
                size={14}
                className={`animate-spin ${
                  isDark ? "text-gray-500" : "text-gray-400"
                }`}
              />
            </div>
          ) : !progress?.courses?.length ? (
            <p
              className={`text-[12px] py-2 ${
                isDark ? "text-gray-500" : "text-gray-500"
              }`}
            >
              Nog niet ingeschreven voor cursussen.
            </p>
          ) : (
            <div className="space-y-2">
              {progress.courses.map((c) => {
                const pct = c.lessons_total
                  ? Math.round((c.lessons_completed / c.lessons_total) * 100)
                  : 0;
                return (
                  <div
                    key={c.id}
                    className={`p-3 rounded-md border ${
                      isDark
                        ? "bg-white/[0.02] border-white/[0.06]"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <BookOpen
                          size={12}
                          className={isDark ? "text-gray-500" : "text-gray-400"}
                        />
                        <span
                          className={`text-[12px] font-medium ${
                            isDark ? "text-gray-200" : "text-gray-800"
                          }`}
                        >
                          {c.title}
                        </span>
                      </div>
                      <span
                        className={`text-[11px] ${
                          isDark ? "text-gray-500" : "text-gray-500"
                        }`}
                      >
                        {c.lessons_completed}/{c.lessons_total} ({pct}%)
                      </span>
                    </div>
                    <div
                      className={`h-1.5 rounded-full overflow-hidden ${
                        isDark ? "bg-white/[0.06]" : "bg-gray-200"
                      }`}
                    >
                      <div
                        className="h-full bg-[#1E4FCF] transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

const Stat = ({ isDark, icon: Icon, label, value }) => (
  <div
    className={`p-2.5 rounded-md ${
      isDark ? "bg-white/[0.03]" : "bg-gray-50"
    }`}
  >
    <div
      className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold ${
        isDark ? "text-gray-600" : "text-gray-400"
      }`}
    >
      <Icon size={10} /> {label}
    </div>
    <div
      className={`text-[12px] mt-0.5 ${
        isDark ? "text-gray-200" : "text-gray-800"
      }`}
    >
      {value}
    </div>
  </div>
);

const Th = ({ children, className = "" }) => (
  <th className={`px-3 py-2 font-medium text-[11px] uppercase tracking-wider ${className}`}>
    {children}
  </th>
);

const Td = ({ children, className = "" }) => (
  <td className={`px-3 py-2.5 ${className}`}>{children}</td>
);

const formatRelative = (iso) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const diff = Date.now() - date.getTime();
  const day = 1000 * 60 * 60 * 24;
  if (diff < day) return "vandaag";
  if (diff < 2 * day) return "gisteren";
  if (diff < 7 * day) return `${Math.floor(diff / day)} dagen geleden`;
  return formatDate(iso);
};

const formatDate = (iso) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("nl-BE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export default StudentsTab;
